import React, { useRef, useState, useEffect } from 'react';

const CosmicBackground = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
 
  const [performanceTier, setPerformanceTier] = useState(3);
  const [ritualMode, setRitualMode] = useState(false);

  // Detect performance
  useEffect(() => {
    const concurrency = navigator.hardwareConcurrency || 4;
    const memory = navigator.deviceMemory || 4;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let tier = Math.min(concurrency * memory / 4, 6);
    if (reducedMotion) tier = Math.max(tier - 2, 0);
    setPerformanceTier(tier);
  }, []);

  // Ritual Mode toggle
  useEffect(() => {
    let count = 0;
    const handler = (e) => {
      if (e.key === 'r' || e.key === 'R') {
        count++;
        if (count === 3) {
          setRitualMode((prev) => !prev);
          count = 0;
        }
      } else count = 0;
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const effectiveTier = ritualMode ? 6 : performanceTier;
  const density = [50, 100, 200, 300, 400, 500, 600][effectiveTier];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.z = Math.random() * 1000 + 1;
        this.size = 1 / this.z * 2;
        this.speed = this.size * 0.5;
        this.color = `rgba(255,255,255,${0.1 / this.z})`;
      }
      update() {
        this.z -= this.speed;
        if (this.z < 1) {
          this.z = 1000;
          this.x = Math.random() * canvas.width;
          this.y = Math.random() * canvas.height;
        }
        this.size = 1 / this.z * 2;
      }
      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const drawNebula = () => {
      const grad = ctx.createRadialGradient(canvas.width / 2, canvas.height / 2, 0, canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height));
      grad.addColorStop(0, 'rgba(10,10,20,0.05)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    particles.current = [];
    for (let i = 0; i < density; i++) {
      particles.current.push(new Particle());
    }

    let animationFrame;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawNebula();
      particles.current.forEach((p) => {
        p.update();
        p.draw();
      });
      animationFrame = requestAnimationFrame(animate);
    };
    animate();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);

    let mouseX = 0, mouseY = 0;
    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        mouseX = e.touches[0].clientX;
        mouseY = e.touches[0].clientY;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);

    const pullInterval = setInterval(() => {
      particles.current.forEach((p) => {
        const dx = mouseX - p.x;
        const dy = mouseY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
          p.x += dx / dist * 0.5;
          p.y += dy / dist * 0.5;
        }
      });
    }, 16);

    const scatter = (e) => {
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);

      particles.current.forEach((p) => {
        const dx = clientX - p.x;
        const dy = clientY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 300) {
          p.x += (Math.random() - 0.5) * 50;
          p.y += (Math.random() - 0.5) * 50;
          p.z += Math.random() * 100;
        }
      });
    };
    canvas.addEventListener('click', scatter);
    canvas.addEventListener('touchstart', scatter);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      if (canvas) {
        canvas.removeEventListener('click', scatter);
        canvas.removeEventListener('touchstart', scatter);
      }
      clearInterval(pullInterval);
    };
  }, [effectiveTier, ritualMode, density]);

  return (
    <div className="cosmic-bg">
      <canvas ref={canvasRef} />
    </div>
  );
};

export default CosmicBackground;